<template>
    <div class="home-page">
        <!-- Hero Section -->
        <Section class="hero-section">
            <div class="hero-container">
                <div class="hero-badge">
                    <span class="sloth-emoji">🦥</span>
                    {{ t('home.hero.badge') }}
                </div>
                <h1 class="hero-title">
                    {{ t('home.hero.title_line1') }}<br />{{ t('home.hero.title_line2') }}
                </h1>
                <p class="hero-subtitle">
                    {{ t('home.hero.subtitle') }}
                </p>
                <div class="hero-actions">
                    <v-btn
                        color="primary"
                        size="x-large"
                        rounded
                        elevation="2"
                        :to="getLocalizedPath('/contact')"
                        tag="a"
                    >
                        {{ t('home.hero.primary_cta') }}
                    </v-btn>
                    <v-btn
                        variant="outlined"
                        size="x-large"
                        rounded
                        :to="getLocalizedPath('/software')"
                        tag="a"
                    >
                        {{ t('home.hero.secondary_cta') }}
                    </v-btn>
                </div>
            </div>
        </Section>

        <!-- Hero banner image (the hacker sloth, near full container width) -->
        <Section class="hero-banner-section">
            <div class="hero-banner">
                <img
                    src="/images/sloth-hacker.webp"
                    alt="A sloth hacking away at lean Rust code"
                    class="hero-banner-image"
                    loading="eager"
                />
            </div>
        </Section>

        <!-- Pillars Section -->
        <Section class="pillars-section">
            <div class="pillars-container">
                <v-row>
                    <v-col
                        v-for="pillar in pillars"
                        :key="pillar.key"
                        cols="12"
                        sm="6"
                        lg="3"
                    >
                        <div class="pillar-card">
                            <div class="pillar-value">
                                {{ t(`home.pillars.${pillar.key}.value`) }}
                            </div>
                            <div class="pillar-label">
                                {{ t(`home.pillars.${pillar.key}.label`) }}
                            </div>
                            <div class="pillar-desc">
                                {{ t(`home.pillars.${pillar.key}.desc`) }}
                            </div>
                            <div class="pillar-benefit">
                                {{ t(`home.pillars.${pillar.key}.benefit`) }}
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </div>
        </Section>

        <!-- Products Section -->
        <Section class="products-section">
            <div class="page-container">
                <header class="section-heading">
                    <h2>{{ t('home.products.title') }}</h2>
                    <p class="section-subtitle">
                        {{ t('home.products.subtitle') }}
                    </p>
                </header>
                <div class="products-cta">
                    <v-btn
                        color="primary"
                        size="large"
                        rounded
                        :to="getLocalizedPath('/software')"
                        tag="a"
                    >
                        {{ t('home.products.see_all') }}
                    </v-btn>
                </div>
            </div>
        </Section>
    </div>
</template>

<script setup lang="ts">
    import { useRoute } from 'vue-router'
    import Section from '@/components/Section.vue'
    import { useTranslations } from '@/composables/useTranslations'
    import { useSEO } from '@/composables/useSEO'

    const { t, currentLanguage } = useTranslations()
    const route = useRoute()

    const pillars = [{ key: 'open' }, { key: 'rust' }, { key: 'tested' }, { key: 'secure' }]

    const getLocalizedPath = (path: string) => {
        const lang = (route.params.lang as string) || currentLanguage.value
        return `/${lang}${path === '/' ? '' : path}`
    }

    useSEO({
        title: () => t('seo.home.title'),
        description: () => t('seo.home.description'),
    })
</script>

<style scoped>
    .home-page {
        padding-top: 80px; /* Account for fixed header */
    }

    .page-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 16px;
    }

    /* Hero Section */
    .hero-section {
        background: linear-gradient(
            180deg,
            rgba(var(--v-theme-surface), 1) 0%,
            rgba(var(--v-theme-surface-variant), 0.3) 100%
        );
        padding: 80px 0;
        contain: layout;
    }

    .hero-container {
        max-width: 900px;
        margin: 0 auto;
        text-align: center;
    }

    .hero-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: rgb(var(--v-theme-primary));
        color: white;
        padding: 8px 20px;
        border-radius: 24px;
        font-size: 0.875rem;
        font-weight: 600;
        margin-bottom: 24px;
    }

    .sloth-emoji {
        font-size: 1.2em;
    }

    .hero-title {
        font-size: clamp(2.5rem, 5vw, 4rem);
        font-weight: 800;
        line-height: 1.1;
        margin: 0 0 24px;
        color: rgb(var(--v-theme-on-surface));
    }

    .hero-subtitle {
        font-size: clamp(1.125rem, 2vw, 1.375rem);
        line-height: 1.6;
        color: rgb(var(--v-theme-on-surface-variant));
        margin: 0 0 40px;
        max-width: 800px;
        margin-left: auto;
        margin-right: auto;
    }

    .hero-actions {
        display: flex;
        gap: 16px;
        justify-content: center;
        flex-wrap: wrap;
        margin-bottom: 40px;
        min-height: 56px;
    }

    /* Hero banner image (hacker sloth) */
    .hero-banner-section {
        padding: 0 0 40px;
    }

    .hero-banner {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 16px;
    }

    .hero-banner-image {
        display: block;
        width: 100%;
        height: auto;
        border-radius: 20px;
    }

    /* Pillars Section */
    .pillars-section {
        padding: 60px 0;
    }

    .pillars-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 16px;
    }

    .pillar-card {
        text-align: center;
        padding: 32px 16px;
    }

    .pillar-value {
        font-size: 3rem;
        font-weight: 800;
        margin-bottom: 8px;
        color: rgb(var(--v-theme-primary));
    }

    .pillar-label {
        font-size: 1.25rem;
        font-weight: 700;
        margin-bottom: 8px;
        color: rgb(var(--v-theme-on-surface));
    }

    .pillar-desc {
        font-size: 1rem;
        color: rgb(var(--v-theme-on-surface-variant));
        margin-bottom: 8px;
    }

    .pillar-benefit {
        font-size: 0.875rem;
        color: rgb(var(--v-theme-primary));
        font-weight: 600;
        margin-top: 8px;
    }

    /* Products Section */
    .products-section {
        background: rgba(var(--v-theme-surface-variant), 0.3);
        padding: 80px 0;
    }

    .section-heading {
        text-align: center;
        margin-bottom: 40px;
    }

    .section-heading h2 {
        font-size: clamp(2rem, 4vw, 3rem);
        font-weight: 700;
        margin: 0 0 16px;
        color: rgb(var(--v-theme-on-surface));
    }

    .section-subtitle {
        font-size: 1.125rem;
        color: rgb(var(--v-theme-on-surface-variant));
        max-width: 700px;
        margin: 0 auto;
        line-height: 1.6;
    }

    .products-cta {
        display: flex;
        justify-content: center;
        margin-top: 24px;
    }

    @media (max-width: 960px) {
        .home-page {
            padding-top: 60px;
        }

        .hero-section {
            padding: 60px 0;
        }

        .pillars-section,
        .products-section {
            padding: 60px 0;
        }
    }
</style>
